import ApiService from "./ApiService";
import { WEB_APP_CODE, WEB_APP_HINTS_CODE } from "./PayPlanUtil";

function readBalance(payload) {
    const n = Number(payload?.creditBalance);
    return Number.isFinite(n) && n >= 0 ? n : 0;
}

function failureMessage(data, fallback) {
    if (data?.code === "FAILURE") {
        return data.message || fallback;
    }
    return null;
}

/**
 * Web credit wallets under `/home/credit`:
 * - {@link WEB_APP_CODE} — Premium play unlocks
 * - {@link WEB_APP_HINTS_CODE} — ghost guide
 */
const HomeCreditService = {
    /** @deprecated Prefer {@link #WEB_APP_CODE}. */
    SLIDING_APP_CODE: WEB_APP_CODE,
    WEB_APP_CODE,
    WEB_APP_HINTS_CODE,

    /**
     * Idempotent premium-puzzle unlock key: `{appCode}:{puzzleId}`.
     * @param {string} appCode
     * @param {string} puzzleId
     */
    puzzleUnlockFeatureUsed(appCode, puzzleId) {
        return `${appCode}:${puzzleId}`;
    },

    async fetchBalance(appCode = WEB_APP_CODE) {
        const data = await ApiService.getData("/home/credit/balance", { appCode });
        const err = failureMessage(data, "Failed to load credit balance");
        if (err) throw new Error(err);
        return {
            appCode: data?.appCode || appCode,
            creditBalance: readBalance(data),
        };
    },

    /**
     * Deduct credits before a paid feature. Puzzle unlocks (`appCode:uuid`) charge once;
     * repeats return `creditsConsumed: 0`.
     */
    async consumeCredits({ appCode = WEB_APP_CODE, featureUsed, creditsConsumed = 1 } = {}) {
        const data = await ApiService.postData("/home/credit/consume", {
            appCode,
            featureUsed,
            creditsConsumed,
        });
        const err = failureMessage(data, "Failed to spend credits");
        if (err) throw new Error(err);
        return {
            ...data,
            creditBalance: readBalance(data),
            creditsConsumed: Number(data?.creditsConsumed) || 0,
        };
    },

    /**
     * Charge 1 play credit to open a Premium puzzle (once per user + puzzleId).
     * Uses {@code appCode=web_app}.
     * @param {string} puzzleId
     */
    async consumePremiumPlay(puzzleId) {
        const appCode = WEB_APP_CODE;
        return HomeCreditService.consumeCredits({
            appCode,
            featureUsed: HomeCreditService.puzzleUnlockFeatureUsed(appCode, puzzleId),
            creditsConsumed: 1,
        });
    },

    /**
     * Charge 1 play credit to open a premium sliding puzzle (once per user + slidingId).
     * @param {string} slidingId
     */
    async consumeSlidingPremiumPlay(slidingId) {
        return HomeCreditService.consumePremiumPlay(slidingId);
    },

    /**
     * Spend 1 credit from {@code web_app_hints} (board hint / placement guide).
     * Per-use — not puzzle-unlock idempotent.
     */
    async consumeWebAppHint() {
        return HomeCreditService.consumeCredits({
            appCode: WEB_APP_HINTS_CODE,
            featureUsed: "hint",
            creditsConsumed: 1,
        });
    },

    /**
     * Spend 1 credit from {@code web_app_hints} for a sliding board hint.
     * Must use appCode {@link WEB_APP_HINTS_CODE} (not {@link WEB_APP_CODE}).
     */
    async consumeSlidingGhostHint() {
        return HomeCreditService.consumeWebAppHint();
    },

    /** Jigsaw Muse encouragement wallet. Same app code as the Android app. */
    JIGSAW_MUSE_HINTS_CODE: "jigsaw_muse_hints",

    deviceZone() {
        try {
            const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            return zone || "America/Montreal";
        } catch {
            return "America/Montreal";
        }
    },

    async fetchJigsawStreak() {
        const data = await ApiService.getData("/home/credit/streak", {
            appCode: HomeCreditService.JIGSAW_MUSE_HINTS_CODE,
            zone: HomeCreditService.deviceZone(),
        });
        const err = failureMessage(data, "Failed to load daily coins");
        if (err) throw new Error(err);
        return data;
    },

    async claimJigsawStreak({ repair } = {}) {
        const body = {
            appCode: HomeCreditService.JIGSAW_MUSE_HINTS_CODE,
            zone: HomeCreditService.deviceZone(),
        };
        if (repair) body.repair = repair;
        const data = await ApiService.postData("/home/credit/streak/claim", body);
        const err = failureMessage(data, "Failed to claim daily coins");
        if (err) throw new Error(err);
        return data;
    },

    /**
     * Spend encouragement coins to place one piece at home.
     * Server charges the configured cost for {@code placement_guide_hint} (5 coins).
     */
    async consumeJigsawPlacePiece() {
        return HomeCreditService.consumeCredits({
            appCode: HomeCreditService.JIGSAW_MUSE_HINTS_CODE,
            featureUsed: "placement_guide_hint",
            creditsConsumed: 5,
        });
    },

    async grantJigsawMilestone({ puzzleId, grid, kind }) {
        const data = await ApiService.postData("/home/credit/milestone", {
            appCode: HomeCreditService.JIGSAW_MUSE_HINTS_CODE,
            puzzleId,
            grid,
            kind,
        });
        const err = failureMessage(data, "Failed to add coins");
        if (err) throw new Error(err);
        return data;
    },
};

export default HomeCreditService;
